import { ContactForm } from '../../components/forms/ContactForm';
import { DemoPopup } from '../../components/forms/DemoPopup';
import { Button } from '../../components/ui/button';
import {
  Kanban,
  LayoutGrid,
  GanttChart,
  Timer,
  Users,
  FolderKanban,
  BarChart2,
  CheckCircle2,
} from 'lucide-react';

const features = [
  {
    icon: LayoutGrid,
    title: 'Kanban Boards',
    description: 'Visualize workflows and track task progress with intuitive drag-and-drop boards.',
  },
  {
    icon: GanttChart,
    title: 'Gantt Charts',
    description: 'Plan timelines, set dependencies, and visualize project schedules at a glance.',
  },
  {
    icon: Timer,
    title: 'Time Tracking',
    description: 'Track time spent on tasks and projects for accurate billing and reporting.',
  },
  {
    icon: Users,
    title: 'Team Collaboration',
    description: 'Comments, mentions, file sharing, and real-time updates for seamless teamwork.',
  },
  {
    icon: FolderKanban,
    title: 'Resource Management',
    description: 'Allocate resources, manage workloads, and prevent team burnout.',
  },
  {
    icon: BarChart2,
    title: 'Reports & Analytics',
    description: 'Detailed insights into project progress, team performance, and more.',
  },
];

const benefits = [
  'Deliver projects on time and within budget',
  'Improve team productivity by 40%',
  'Real-time visibility into project status',
  'Better resource allocation and planning',
  'Reduce project management overhead',
  'Seamless integration with other tools',
];

export const ProjectPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="project-page">
      {/* Hero Banner */}
      <section className="py-20 md:py-28 hero-glow" data-testid="project-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 animate-fade-in-up">
              Project Management
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-white animate-fade-in-up stagger-1">
              Deliver Projects On Time, Every Time
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mt-6 animate-fade-in-up stagger-2">
              Plan, track, and collaborate on projects with powerful tools designed for modern teams.
            </p>
            <div className="mt-8 animate-fade-in-up stagger-3">
              <DemoPopup
                title="Request Demo for Project Management"
                formType="Project Management Demo"
                defaultSolution="Project Management"
                trigger={
                  <Button
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-medium glow-primary glow-primary-hover btn-shine"
                    data-testid="project-hero-demo-btn"
                  >
                    Request Demo
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 md:py-32" data-testid="project-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
              Key Features
            </h2>
            <p className="text-muted-foreground text-lg mt-4">
              Tools to keep your projects on track
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`glass rounded-2xl p-8 card-hover animate-fade-in-up stagger-${index + 1}`}
                data-testid={`project-feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="feature-icon mb-6">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold font-heading text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 md:py-32 bg-secondary/30" data-testid="project-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
                Why Choose Our Project Management?
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Our solution helps teams of all sizes deliver projects efficiently and collaboratively.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="animate-fade-in-up stagger-2">
              <div className="rounded-2xl overflow-hidden border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80"
                  alt="Project Team"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Form Section */}
      <section className="py-24 md:py-32" data-testid="project-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12 animate-fade-in-up">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
                Request a Demo for Project Management
              </h2>
              <p className="text-muted-foreground text-lg mt-4">
                Let us show you how to deliver projects more efficiently.
              </p>
            </div>

            <div className="glass rounded-2xl p-8 animate-fade-in-up stagger-1" data-testid="project-demo-form-wrapper">
              <ContactForm
                formType="Project Management Demo"
                showInterestedIn={false}
                showTeamSize={true}
                interestedInDefault="Project Management"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectPage;
